/**
 * Child-product permission catalogs for Page Permissions Manager.
 *
 * Grants live in `permissions` with:
 *   platform         = acuitycte | learnpath | acuitylearn | ots | acuitydms   (main = UIS brain, not here)
 *   resource_type    = page | capability | platform
 *   resource_id      = catalog key, or '*' for platform_access
 *   permission_type  = platform_access | page_access | page_configuration | capability
 *   permission_name  = optional human title (not the catalog key)
 *
 * platform_access is the product enable switch — without an allow, other grants
 * for that platform must not apply.
 *
 * @see docs/permissions-platform-and-pages.md
 * @see docs/acuitycte-integration/permissions.txt
 */

export const MAIN_PLATFORM = 'main';
export const PLATFORM_ACCESS_RESOURCE_ID = '*';

function humanizePermissionKey(id) {
  if (!id || typeof id !== 'string') return '';
  return id
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** @type {{ id: string, label: string }[]} */
export const PLATFORM_PRODUCTS = [
  { id: 'acuitycte', label: 'Acuity CTE' },
  { id: 'learnpath', label: 'LearnPath' },
  { id: 'acuitylearn', label: 'Acuity Learn' },
  { id: 'ots', label: 'OTS' },
  { id: 'acuitydms', label: 'AcuityDMS' },
];

const ACUITYCTE_PAGES = [
  'admin-directory',
  'all-hours',
  'assignment',
  'assignment-builder',
  'assignment-submissions-auditor',
  'community',
  'componentsubmissions',
  'course-builder',
  'course-enroll-manager',
  'course-viewer',
  'courses',
  'courses-previewer',
  'create-field-experience',
  'dashboard',
  'email-templator',
  'fe-event-attendance',
  'feeventregistrations',
  'fieldexperienceevents',
  'reviewersbyevent',
  'home',
  'my-courses',
  'my-courses-manager',
  'my-directory',
  'my-events',
  'my-hours',
  'newsletter',
  'newsletter-viewer',
  'open-fe-event-survey',
  'open-registration-matcher',
  'page-access-manager',
  'project-builder',
  'projects-directory',
  'register-a-student-for-an-event',
  'register-for-field-experience',
  'request-fe-hours-by-external-activity',
  'relationships',
  'saf-form',
  'schedule-builder',
  'taxonomy',
  'user-management',
  'users-csv-import',
  'waiver-forms',
].map((id) => ({ id, label: humanizePermissionKey(id) }));

const ACUITYCTE_CAPABILITIES = [
  'assignment-builder.CreateTaxonomy',
  'assignment-builder.GenerateDraft',
  'assignment-builder.UseAssistant',
  'course-builder.ComponentNotificationSettings',
  'course-builder.EditSharedCourseContent',
  'course-builder.GenerateAssignmentDraft',
  'course-builder.GenerateOutline',
  'course-builder.ManageCourseSubjects',
  'course-builder.ModlockControl',
  'course-builder.UpdateCreator',
  'course-builder.UseAssistant',
  'login.BlockResetPassword',
  'my-courses-manager.DeleteCourse',
  'my-courses-manager.OpenCourseBuilder',
  'my-events.DownloadDocuments',
  'my-events.DownloadRegistrations',
  'my-events.OpenSAFForm',
  'my-events.ViewAllEvents',
  'my-events.ViewRegistrations',
  'newsletter.ViewAllAccounts',
  'newsletter.ViewStudentsFilter',
  'register-for-field-experience.SelfRegister',
  /**
   * Hour waiver / external activity request pipeline (RequestFEHoursByExternalActivity).
   * page_access opens the page. Caps map to approval levels (supervisor stays guest-link + code).
   */
  {
    id: 'request-fe-hours-by-external-activity.SelfSubmit',
    label: 'Request FE Hours Self Submit',
    description:
      'Create a new hour-waiver / external-activity request as the student. Requires an in-window ' +
      'current UGP (enrollment context). Replaces accesscards.role = Student for this page. ' +
      'Does not grant school/system admin decide steps.',
  },
  {
    id: 'request-fe-hours-by-external-activity.DecideAsTor',
    label: 'Request FE Hours Decide As Tor',
    description:
      'Act on the Teacher-of-Record step for an hour waiver (approve/deny/comment). Usually ' +
      'combined with being TOR on the student’s membership or selected on the waiver. ' +
      'Replaces informal TOR access via enrollment relationships alone when you want revoke-by-deny.',
  },
  {
    id: 'request-fe-hours-by-external-activity.DecideAsSchoolAdmin',
    label: 'Request FE Hours Decide As School Admin',
    description:
      'School-admin approval step on hour waivers. Scope still limited by the caller’s school ' +
      'scope (ancestors / scope.*) relative to the student’s membership. Replaces accesscards.role ' +
      'containing Admin (school-level) for this pipeline.',
  },
  {
    id: 'request-fe-hours-by-external-activity.DecideAsSystemAdmin',
    label: 'Request FE Hours Decide As System Admin',
    description:
      'Final system-admin approval / verify step on hour waivers (and bulk actions on Waiver Forms). ' +
      'Replaces Super - Admin / System Admin role checks for this pipeline. Prefer with scope.Global ' +
      'or wide org scope when cross-school review is intended.',
  },
  /**
   * Register-a-student manager: full event roster (skip school-scope filter on by-event).
   * Does not open the page (still needs page_access). Does not widen event list or student picker.
   */
  {
    id: 'register-a-student-for-an-event.ViewAllRegistrations',
    label: 'Register A Student View All Registrations',
    description:
      'On Register-a-student for an event: show every registration on an event the caller can ' +
      'already open — not only students in their school/group scope. For teachers or staff who ' +
      'need the full roster. Does not grant page access by itself, and does not expand which ' +
      'events or which students appear in the picker. Default without this cap: roster stays ' +
      'school-scoped (ancestors / scope.*).',
  },
  'saf-form.MayEditSAF',
  'saf-form.MayViewSAF',
  /**
   * Field Experience identity badges (permission_type = capability).
   * CanBeLeadInstructor — searchable lead-instructor candidates (not platform_access alone).
   * CoordinatorInvite — auto-invite reviewers on create (replaces accesscards.role = Coordinator).
   * CanVerifyHours — sign/verify hours on feeventregistrations (Attendance; not bound to page_access alone).
   * Membership-scoped: grantee_type must be groups_periods or user_groups_periods (not users).
   */
  'fieldexperienceevents.CanBeLeadInstructor',
  'fieldexperienceevents.CoordinatorInvite',
  /**
   * RegistrarNotify — email when request is approved (“potential event”) and when event goes official/live.
   * Replaces accesscards.role = Registrar. School-scoped via in-window UGP (same as CoordinatorInvite).
   */
  'fieldexperienceevents.RegistrarNotify',
  'feeventregistrations.CanVerifyHours',
  /**
   * reviewersbyevent.CanDecide — act on existing reviewer rows (approve/deny/force-approve).
   * Separate from CoordinatorInvite (who gets invited). Deny stops acting even with a row.
   */
  'reviewersbyevent.CanDecide',
  /**
   * directory.AppearInStudentQueries — memberships that appear in staff “student” pickers
   * (Register-a-student, etc.). Grant on student GPs/UGPs (any group name). Replaces the
   * legacy implicit cohort of “has a valid enrollment row” — not accesscards.role = Student.
   * Membership-scoped: groups_periods | user_groups_periods only.
   * Consumers also require the UGP to be in-window (same period / override_period rules as login).
   */
  {
    id: 'directory.AppearInStudentQueries',
    label: 'Directory Appear In Student Queries',
    description:
      'Marks which memberships may appear in staff “student” pickers and similar directory ' +
      'queries (e.g. Register-a-student). Grant on student GPs or UGPs — any group name; this is ' +
      'not tied to a “Student” role label. Membership-only (GP/UGP). ' +
      'Having the badge alone is not enough: Acuity only lists UGPs that are currently in-window, ' +
      'using the same validity rules as login / current-membership — active membership, then ' +
      'override_period dates when set, otherwise the group-period’s period start/end. Expired or ' +
      'future terms do not show. Callers still only see students inside their school scope.',
  },
  /**
   * Replaces accesscards.global — product-wide school scope (any group in Acuity CTE).
   * Narrower options: SameLevelChildren, OrganizationChildren, ListedGroups.
   */
  {
    id: 'scope.Global',
    description:
      'Product-wide school scope: any group in Acuity CTE. Skips the school filter entirely. ' +
      'Not “my district.” Prefer SameLevelChildren, ParentPeerChildren, OrganizationChildren, or ' +
      'ListedGroups for bounded admin scope.',
  },
  /**
   * Same-parent siblings + each sibling’s nested children (groups.id).
   * District A → School A / School B / District Admin: grant on District Admin → includes School A/B.
   * School Admin under School A only: peers under School A — not sibling schools under the district.
   */
  {
    id: 'scope.SameLevelChildren',
    label: 'Scope Same Level Children',
    description:
      'Same-level peers and their children. Expands school scope to every group under the same ' +
      'parent as the current UGP (sibling teams + everything nested under those siblings). ' +
      'Example: District A with children School A, School B, District Admin — admins on District ' +
      'Admin see School A/B. If the admin team sits under School A instead, this only covers ' +
      'peers under School A (not School B) — use ParentPeerChildren for sibling schools. ' +
      'Membership-only (GP/UGP).',
  },
  /**
   * Peers of the membership’s parent + those peers’ children.
   * Typical in-school admin (Admin A under School A) who should see School B.
   */
  {
    id: 'scope.ParentPeerChildren',
    label: 'Scope Parent Peer Children',
    description:
      'Parent’s peers and their children. Walk up one level to the current UGP’s parent, then ' +
      'include that parent’s sibling groups and everything nested under them. Example: Admin A ' +
      'under School A under District A → School A’s peers (School B, District Admin, …) and their ' +
      'students. Most school admins live under the school (not as District Admin peers of schools), ' +
      'so this is the usual way to see sibling schools without granting whole-org OrganizationChildren. ' +
      'Membership-only (GP/UGP).',
  },
  /**
   * Everything under the organization / system GP (full org branch).
   */
  {
    id: 'scope.OrganizationChildren',
    label: 'Scope Organization Children',
    description:
      'Organization children. Expands school scope to every group under the membership’s ' +
      'organization root. Wider than ParentPeerChildren when schools sit under a region inside a ' +
      'larger org (ParentPeerChildren stops at the parent’s peer level). Membership-only (GP/UGP).',
  },
  /**
   * Explicit allowlist via permission rules (group ids and/or groups_periods ids).
   */
  {
    id: 'scope.ListedGroups',
    label: 'Scope Listed Groups',
    description:
      'Exact groups only — no inheritance and no look-down. rules.scope_group_ids / ' +
      'scope_groups_periods_ids must match the group id itself (events tagged with that id, or ' +
      'memberships whose group_id is that id). Listing a school/org does not include nested teams ' +
      'under it (e.g. a Students team under that school). List each team you need, or use ' +
      'ParentPeerChildren / OrganizationChildren when you want child groups included. ' +
      'Important: every other capability on this membership applies uniformly to all listed groups — ' +
      'you cannot set different permissions per listed school here. For different access at different ' +
      'schools, use a separate UGP (membership in that school’s group) instead. ' +
      'Mutually exclusive with scope.Global on the same GP/UGP. Membership-only (GP/UGP).',
  },
  'taxonomy.CreateConcentration',
  'taxonomy.CreateCoreSubjects',
  'taxonomy.CreateStandardsSkillsObjectives',
  'taxonomy.EditConcentrations',
  'taxonomy.EditCoreSubjects',
  'taxonomy.EditStandardsSkillsObjectives',
].map((entry) => {
  const id = typeof entry === 'string' ? entry : entry.id;
  return {
    id,
    label: (typeof entry === 'object' && entry.label) || humanizePermissionKey(id),
    ...(typeof entry === 'object' && entry.description
      ? { description: entry.description }
      : {}),
  };
});

/** Capabilities that may only be granted to GP / UGP (not bare users). */
export const MEMBERSHIP_ONLY_CAPABILITIES = new Set([
  'fieldexperienceevents.CanBeLeadInstructor',
  'fieldexperienceevents.CoordinatorInvite',
  'fieldexperienceevents.RegistrarNotify',
  'feeventregistrations.CanVerifyHours',
  'reviewersbyevent.CanDecide',
  'directory.AppearInStudentQueries',
  'scope.SameLevelChildren',
  'scope.ParentPeerChildren',
  'scope.OrganizationChildren',
  'scope.ListedGroups',
]);

/**
 * Capabilities granted to durable groups only (groups.id — not GP / UGP / users).
 * LearnPath school marker: the org/team group itself appears as a School.
 */
export const GROUP_ONLY_CAPABILITIES = new Set(['school.AppearAsSchool']);

/** LearnPath identity / action badges — membership only (GP / UGP). */
export const LEARNPATH_MEMBERSHIP_CAPABILITIES = new Set([
  'role.IsSuperAdmin',
  'role.IsAdmin',
  'role.IsTeacher',
  'role.IsStudent',
  'school.ManageSchools',
  'school.ManageParticipants',
  'program.ManagePrograms',
  'program.ManageEnrollments',
  'training.CreateTrainings',
  'training.EditTrainings',
  'taxonomy.CreateTaxonomy',
  'people.ListDirectory',
  'scope.Global',
  'scope.ListedGroups',
]);

/**
 * LearnPath identity role badges — at most one per grantee (UGP or GP).
 * Super Admin vs Teacher vs Admin vs Student are alternate identities, not stackable flags.
 */
export const LEARNPATH_EXCLUSIVE_ROLE_CAPABILITIES = new Set([
  'role.IsSuperAdmin',
  'role.IsAdmin',
  'role.IsTeacher',
  'role.IsStudent',
]);

export const ACUITYLEARN_EXCLUSIVE_ROLE_CAPABILITIES = new Set([
  'role.IsSuperAdmin',
  'role.IsAdmin',
  'role.IsTeacher',
  'role.IsStudent',
  'role.IsCurriculumDeveloper',
  'role.IsCounselor',
  'role.IsParent',
]);

/** Products that use exclusive membership role.* badges and school.AppearAsSchool. */
export const ROLE_BADGE_PLATFORMS = new Set(['learnpath', 'acuitylearn']);
export const SCHOOL_MARKER_PLATFORMS = new Set(['learnpath', 'acuitylearn']);

export function exclusiveRoleCapabilitiesForPlatform(productId) {
  if (productId === 'acuitylearn') return ACUITYLEARN_EXCLUSIVE_ROLE_CAPABILITIES;
  if (productId === 'learnpath') return LEARNPATH_EXCLUSIVE_ROLE_CAPABILITIES;
  return new Set();
}

/**
 * School visibility scope caps — at most one per GP/UGP grantee.
 * Runtime picks a single scope; overlapping grants are confusing and silently override.
 */
export const LEARNPATH_EXCLUSIVE_SCOPE_CAPABILITIES = new Set([
  'scope.Global',
  'scope.ListedGroups',
]);

export const ACUITYLEARN_EXCLUSIVE_SCOPE_CAPABILITIES = new Set([
  'scope.Global',
  'scope.ListedGroups',
]);

export const ACUITYCTE_EXCLUSIVE_SCOPE_CAPABILITIES = new Set([
  'scope.Global',
  'scope.SameLevelChildren',
  'scope.ParentPeerChildren',
  'scope.OrganizationChildren',
  'scope.ListedGroups',
]);

/** Products where scope.* caps are mutually exclusive per membership grantee. */
export const SCOPE_EXCLUSIVE_PLATFORMS = new Set(['learnpath', 'acuitylearn', 'acuitycte']);

export function exclusiveScopeCapabilitiesForPlatform(productId) {
  if (productId === 'acuitycte') return ACUITYCTE_EXCLUSIVE_SCOPE_CAPABILITIES;
  if (productId === 'acuitylearn') return ACUITYLEARN_EXCLUSIVE_SCOPE_CAPABILITIES;
  if (productId === 'learnpath') return LEARNPATH_EXCLUSIVE_SCOPE_CAPABILITIES;
  return new Set();
}

/** Acuity Learn identity / action badges — membership only (GP / UGP). */
export const ACUITYLEARN_MEMBERSHIP_CAPABILITIES = new Set([
  'role.IsSuperAdmin',
  'role.IsAdmin',
  'role.IsTeacher',
  'role.IsStudent',
  'role.IsCurriculumDeveloper',
  'role.IsCounselor',
  'role.IsParent',
  'parent.ViewStudentChat',
  'parent.MessageInStudentChat',
  'parent.ViewStudentsCourseEnrollments',
  'parent.ViewStudentsCourseContent',
  'parent.ViewStudentContact',
  'parent.UpdateStudentContact',
  'parent.ViewGuardianContact',
  'parent.UpdateGuardianContact',
  'parent.ManageStudentGuardianRelationships',
  'student.AddSelfGuardianRelationships',
  'people.GrantContactPermissions',
  'parent.MessageStudentsOffice',
  'people.UpdateOwnContact',
  'people.EditUserData',
  'people.EditUserDataMaster',
  'people.ViewContact',
  'people.UpdateContact',
  'school.CreateSchools',
  'school.EditSchools',
  'school.RemoveSchools',
  'school.AddUsers',
  'school.OverrideAddUserPermissions',
  'school.EditGradeScale',
  'school.EditSchoolCalendar',
  'messages.ReceiveMessages',
  'messages.SendMessages',
  'messages.ReplyMessages',
  'dm.Send',
  'dm.Reply',
  'dm.SendStudentTeacher',
  'dm.SendStudentRelationship',
  'dm.ViewOthersInboxes',
  'people.ViewPersonLinks',
  'people.SearchPeople',
  'people.StudentStaffChat',
  'people.StaffStudentChat',
  'people.PersonLinks',
  'school.ViewUsers',
  'course.ViewCourses',
  'course.ViewMasterCatalog',
  'course.ViewOthersCourses',
  'course.CreateCourses',
  'course.CreateMasterCourses',
  'course.EditMasterCourses',
  'course.ProposeMasterCourses',
  'course.ProposeToCatalog',
  'course.UpdateCatalogAccess',
  'course.EditCourses',
  'course.ReviewProposals',
  'course.ReviewCatalogProposals',
  'course.TeachEnrolled',
  'course.TeachWithoutEnrollment',
  'course.PostAnnouncements',
  'course.OverrideActivityDefaults',
  'course.UseBuilderAssistant',
  'course.BuilderAssistantHistory',
  'course.CourseBreaker',
  'course.RemoveCourses',
  'course.RemoveMasterCourses',
  'course.AssignToSchools',
  'course.ProposeAssignToSchool',
  'course.PlaceOnSchool',
  'course.CopyCourses',
  'course.SetSchoolCopyEdit',
  'course.EditDespiteModLock',
  'course.ManageEnrollments',
  'learning.ViewOwnEnrollments',
  'learning.OpenActivities',
  'learning.SubmitWork',
  'learning.ReplyOnProgress',
  'library.ViewLibrary',
  'library.ViewOthersSources',
  'library.AddSources',
  'library.EditSources',
  'library.ProposeSources',
  'library.ReviewSources',
  'library.RemoveSources',
  'library.AnalyzeSources',
  'library.ManageApprovedCatalogs',
  'taxonomy.CreateTaxonomy',
  'taxonomy.EditTaxonomy',
  'taxonomy.RemoveTaxonomy',
  'scope.Global',
  'scope.ListedGroups',
]);

const ALL_GRANTEE_TYPES = ['users', 'groups_periods', 'user_groups_periods'];
const MEMBERSHIP_GRANTEE_TYPES = ['groups_periods', 'user_groups_periods'];
const GROUP_GRANTEE_TYPES = ['groups'];

/** Allowed grantee_type values for a platform catalog key. */
export function allowedGranteeTypesForPlatformKey(productId, permissionType, resourceId) {
  if (
    SCHOOL_MARKER_PLATFORMS.has(productId) &&
    permissionType === 'capability' &&
    GROUP_ONLY_CAPABILITIES.has(resourceId)
  ) {
    return GROUP_GRANTEE_TYPES;
  }
  if (
    productId === 'learnpath' &&
    permissionType === 'capability' &&
    LEARNPATH_MEMBERSHIP_CAPABILITIES.has(resourceId)
  ) {
    return MEMBERSHIP_GRANTEE_TYPES;
  }
  if (
    productId === 'acuitylearn' &&
    permissionType === 'capability' &&
    ACUITYLEARN_MEMBERSHIP_CAPABILITIES.has(
      canonicalPlatformResourceId(productId, permissionType, resourceId)
    )
  ) {
    return MEMBERSHIP_GRANTEE_TYPES;
  }
  if (
    productId === 'acuitycte' &&
    permissionType === 'capability' &&
    MEMBERSHIP_ONLY_CAPABILITIES.has(resourceId)
  ) {
    return MEMBERSHIP_GRANTEE_TYPES;
  }
  return ALL_GRANTEE_TYPES;
}

const LEARNPATH_CAPABILITIES = [
  {
    id: 'role.IsSuperAdmin',
    label: 'Is Super Admin',
    description:
      'LearnPath identity badge: product-wide Super Admin. Does not open pages by itself. ' +
      'Grant on a System / platform team GP or UGP (in-window). Not school-bound — use with ' +
      'platform_access and school.ManageSchools for the bootstrap Super Admin. Membership-only (GP/UGP). ' +
      'Mutually exclusive with other role.* badges on the same GP/UGP — use a separate UGP for Teacher/Admin/Student.',
  },
  {
    id: 'role.IsAdmin',
    label: 'Is Admin',
    description:
      'LearnPath identity badge: school-level Admin. Does not open pages by itself. ' +
      'Usually granted on a school Admin team GP/UGP. Membership-only (GP/UGP). ' +
      'Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsTeacher',
    label: 'Is Teacher',
    description:
      'LearnPath identity badge: Teacher. Does not open pages by itself. Membership-only (GP/UGP). ' +
      'Mutually exclusive with other role.* badges on the same GP/UGP — Super Admin needs its own UGP.',
  },
  {
    id: 'role.IsStudent',
    label: 'Is Student',
    description:
      'LearnPath identity badge: Student / learner. Does not open pages by itself. Membership-only (GP/UGP). ' +
      'Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'school.AppearAsSchool',
    label: 'Appear As School',
    description:
      'Marks a UIS group (organization or team) as a School in LearnPath. Grant on the ' +
      'durable group itself (grantee_type = groups) — not a groups_period and not a UGP. ' +
      'LearnPath lists these groups in the school picker. Periods and enrollments stay in UIS; ' +
      'this flag only decides which groups count as schools for the product. Prefer organization ' +
      'groups (or school-level teams under a district). Still requires platform_access for LearnPath ' +
      'on some membership before other product grants apply.',
  },
  {
    id: 'school.ManageSchools',
    label: 'Manage Schools',
    description:
      'May create, edit, and remove LearnPath schools. Create may link an existing UIS group by name ' +
      '(grant school.AppearAsSchool) or create a new organization with display_label "School", Permanent ' +
      'period, and AppearAsSchool. Remove only revokes AppearAsSchool (group stays in UIS). Typically ' +
      'paired with role.IsSuperAdmin. Membership-only (GP/UGP). Does not replace platform_access. ' +
      'Does not grant school.ManageParticipants — grant that separately to add learners/instructors.',
  },
  {
    id: 'school.ManageParticipants',
    label: 'Manage Participants',
    description:
      'May add / manage school roster participants in LearnPath (Learners / Instructors on role teams, ' +
      'platform_access, credentials). Previously folded under Manage Schools — now a separate capability. ' +
      'Still needs page_access.schools (or Manage Schools) to open the school page. Membership-only (GP/UGP). ' +
      'Does not replace platform_access or Manage Schools.',
  },
  {
    id: 'program.ManagePrograms',
    label: 'Manage Programs',
    description:
      'May create, edit, and archive LearnPath programs (guided learning packages with AI script + goals) ' +
      'in the LearnPath product database. Also unlocks the Programs page by default (page_access.programs) ' +
      'and the Activity Library by default (page_access.library follows programs unless a library row exists). ' +
      'Assigning learners to a program requires program.ManageEnrollments (separate). Membership-only (GP/UGP). ' +
      'Does not replace platform_access.',
  },
  {
    id: 'program.ManageEnrollments',
    label: 'Manage Program Enrollments',
    description:
      'May assign / revoke learners on a LearnPath program (program_enrollments: UIS user + UGP ↔ program). ' +
      'Does not create school roster participants — use school.ManageParticipants for that. ' +
      'Does not edit program script/goals — use program.ManagePrograms. Membership-only (GP/UGP). ' +
      'Does not replace platform_access.',
  },
  {
    id: 'training.CreateTrainings',
    label: 'Create Trainings',
    description:
      'May create LearnPath training catalog rows (title, subtitle, code, script, goals). ' +
      'Also defaults page_access.trainings on when no explicit trainings page row exists. ' +
      'Taking a training only needs page_access.trainings. Membership-only (GP/UGP). ' +
      'In-app create UI may be deferred — SQL seed is fine for now.',
  },
  {
    id: 'training.EditTrainings',
    label: 'Edit Trainings',
    description:
      'May edit / archive LearnPath training catalog rows (title, subtitle, script, goals, status). ' +
      'Also defaults page_access.trainings on when no explicit trainings page row exists. ' +
      'Taking a training only needs page_access.trainings. Membership-only (GP/UGP). ' +
      'Does not replace Create Trainings.',
  },
  {
    id: 'taxonomy.CreateTaxonomy',
    label: 'Create Taxonomy',
    description:
      'May create UIS taxonomy subjects and concentrations from LearnPath (e.g. program subject picker). ' +
      'Creates shared or org-scoped taxonomy rows via UIS. Membership-only (GP/UGP). ' +
      'Does not replace platform_access or Manage Programs.',
  },
  {
    id: 'people.ListDirectory',
    label: 'List Directory (People)',
    description:
      'May open the LearnPath People directory (/people). On Target, check which roles to list; stored as ' +
      'metadata.list_roles: students | instructors | admins | super_admins (not in Rules — Rules is for ' +
      'vendor / ListedGroups only). Directory rows follow schoolScope. Defaults page_access.people on when ' +
      'any role is checked and no explicit people page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'scope.Global',
    label: 'Scope Global',
    description:
      'Platform-wide school visibility in LearnPath: all groups marked Appear As School. ' +
      'For Super Admin / platform admins who are not bound to one school. Membership-only (GP/UGP). ' +
      'Mutually exclusive with scope.ListedGroups on the same GP/UGP — Global wins at runtime. ' +
      'Does not grant role badges or Manage Schools by itself.',
  },
  {
    id: 'scope.ListedGroups',
    label: 'Scope Listed Groups',
    description:
      'Selected-school visibility only. Set rules.scope_group_ids (preferred) or ' +
      'scope_groups_periods_ids to the school groups this admin may see. No inheritance — list each ' +
      'school group id. Important: every other capability on this membership applies uniformly to all ' +
      'listed schools — you cannot set different permissions per listed school here. For different ' +
      'access at different schools, use a separate UGP (membership in that school’s group) instead. ' +
      'Per-school permission differences may be supported later; for now one UGP per school role is ' +
      'the recommended pattern. Mutually exclusive with scope.Global on the same GP/UGP. ' +
      'Membership-only (GP/UGP). Use for platform admins over a subset of schools with the same role.',
  },
].map((entry) => ({
  id: entry.id,
  label: entry.label || humanizePermissionKey(entry.id),
  ...(entry.description ? { description: entry.description } : {}),
}));

const ACUITYCTE_PAGE_CONFIGURATIONS = [
  { id: 'home.widgets', label: 'Home widgets' },
  {
    id: 'request-fe-hours-by-external-activity.ApprovalPipeline',
    label: 'Request FE Hours Approval Pipeline',
    description:
      'Default approval ladder for hour-waiver / external-activity requests. Stored as ' +
      'a configuration JSON array of slots [{rank,key,label,required,channel}]. ' +
      'Acuity snapshots this onto each waiver at create; runtime uses the snapshot. ' +
      'Grant to everyone who needs the page (e.g. product-wide GP). The Request FE Hours ' +
      'page fails closed if no matching ApprovalPipeline page_configuration is found.',
  },
];

const LEARNPATH_PAGES = [
  {
    id: 'learn',
    label: 'Learn',
    description:
      'LearnPath learner home (chat / enrolled programs). Defaults on with platform_access when no ' +
      'explicit page_access.learn row exists.',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'LearnPath home / My Account profile for the current session.',
  },
  {
    id: 'schools',
    label: 'Schools',
    description:
      'LearnPath Schools admin page. Opening the route also needs page_access; create/edit/remove ' +
      'still require capability school.ManageSchools.',
  },
  {
    id: 'programs',
    label: 'Programs',
    description:
      'LearnPath Programs page (list / create / edit guided programs). Opening the route needs ' +
      'page_access; create/edit/archive require capability program.ManagePrograms. ManagePrograms ' +
      'defaults page_access on when no explicit page row exists.',
  },
  {
    id: 'library',
    label: 'Library',
    description:
      'LearnPath Activity Library (/library) — browse and link (copy) activities across programs. ' +
      'By default follows page_access.programs (including ManagePrograms default). Grant page_access.library ' +
      'allow/deny explicitly to open Library without Programs, or to block Library while keeping Programs. ' +
      'Linking into a program still needs ManagePrograms or instructor create_activities on that program. ' +
      'Sidebar item id must be "library" so nav can filter by this key.',
  },
  {
    id: 'people',
    label: 'People',
    description:
      'LearnPath People directory (/people) — Learners, Instructors, Admins, Super Admins. ' +
      'Defaults on when people.ListDirectory is allowed with at least one metadata.list_roles entry and no ' +
      'explicit page_access.people row exists. Nested sections follow those roles; directory rows follow ' +
      'schoolScope. Sidebar item id must be "people" so nav can filter by this key.',
  },
  {
    id: 'trainings',
    label: 'Trainings',
    description:
      'LearnPath Trainings (/trainings) — staff walkthroughs (e.g. School setup). ' +
      'Requires an explicit page_access.trainings allow (or training.CreateTrainings / training.EditTrainings, ' +
      'which default this page on). Does not follow ManageParticipants / ManageSchools. ' +
      'Sidebar item id must be "trainings".',
  },
].map((entry) => ({
  id: entry.id,
  label: entry.label || humanizePermissionKey(entry.id),
  ...(entry.description ? { description: entry.description } : {}),
}));

const LEARNPATH_PAGE_CONFIGURATIONS = [
  {
    id: 'app.sidebar',
    label: 'App Sidebar',
    description:
      'LearnPath left navigator. configuration.items[] supports id, label, path, icon, order, ' +
      'hidden, and children[] (same shape) for nesting. Sidebar shows items the user also has ' +
      'page_access for (by id) — e.g. library needs id "library" + page_access.library (or programs default); ' +
      'people needs id "people" + page_access.people (or ListDirectory default). LearnPath nests People ' +
      'children from people.ListDirectory metadata.list_roles. Defaults apply when no config is granted.',
  },
].map((entry) => ({
  id: entry.id,
  label: entry.label || humanizePermissionKey(entry.id),
  ...(entry.description ? { description: entry.description } : {}),
}));

const ACUITYLEARN_CAPABILITIES = [
  {
    id: 'role.IsSuperAdmin',
    label: 'Is Super Admin',
    description:
      'Acuity Learn identity label only — does not grant pages, capabilities, or global school scope. ' +
      'Founder / platform owner — keep one membership; UIS refuses extra Super Admin grants. ' +
      'System Admin is role.IsAdmin plus scope.Global, not this badge. ' +
      'Required to sign in (exactly one role.* per membership). Pair with platform_access, and grant ' +
      'scope.Global / page_access / capabilities on the same GP/UGP. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsAdmin',
    label: 'Is Admin',
    description:
      'Acuity Learn identity label only. School Admin and System Admin share this badge. ' +
      'System-wide powers are this plus scope.Global (and the System Admin recipe), not a second identity. ' +
      'Required to sign in (exactly one role.*). ' +
      'Does not open pages or grant verbs. Non-global memberships also need Appear As School on an ancestor group. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsTeacher',
    label: 'Is Teacher',
    description:
      'Acuity Learn identity label only. Required to sign in (exactly one role.*). ' +
      'Does not open pages or grant verbs. Non-global memberships also need Appear As School on an ancestor group. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsStudent',
    label: 'Is Student',
    description:
      'Acuity Learn identity label. Required to sign in (exactly one role.*). ' +
      'Also defaults page_access.student on when no explicit student page row exists. ' +
      'Today / My Learning / Progress then default on with that door unless denied. ' +
      'Does not grant learning.ViewOwnEnrollments, learning.OpenActivities, or learning.SubmitWork — attach those as separate grants. ' +
      'Non-global memberships also need Appear As School on an ancestor group. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsCurriculumDeveloper',
    label: 'Is Curriculum Developer',
    description:
      'Acuity Learn identity label only. Required to sign in (exactly one role.*). ' +
      'Does not open pages or grant verbs — attach those as separate grants (templates later). ' +
      'Non-global memberships also need Appear As School on an ancestor group. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsCounselor',
    label: 'Is Counselor',
    description:
      'Acuity Learn identity label only. Required to sign in (exactly one role.*). ' +
      'Does not open pages or grant verbs — attach those as separate grants (templates later). ' +
      'Non-global memberships also need Appear As School on an ancestor group. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'role.IsParent',
    label: 'Is Parent',
    description:
      'Acuity Learn identity label on the permanent Parents team. Does not grant sign-in by itself. ' +
      'Does not open pages or grant verbs — attach those as separate grants. ' +
      'Membership-only (GP/UGP). Mutually exclusive with other role.* badges on the same GP/UGP.',
  },
  {
    id: 'parent.ViewStudentChat',
    label: 'View Student Chat',
    description:
      'May read the student + teacher chat on a linked student\'s activity, and the parent Threads page. ' +
      'Only the public channel. The student must be a UIS guardian Person Link. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.MessageInStudentChat',
    label: 'Message In Student Chat',
    description:
      'May post in that same public chat as a parent. Follows the teacher\'s chat switch, and the student must have opened the activity first. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'parent.ViewStudentsCourseEnrollments',
    label: 'View Students Course Enrollments',
    description:
      'List the classes a linked student is enrolled in. A guardian link, or a counselor link at this membership\'s school. ' +
      'Does not open the class outline or the student\'s work. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.ViewStudentsCourseContent',
    label: 'View Students Course Content',
    description:
      'Open a linked student\'s class: the published outline and, for now, that student\'s work. ' +
      'Does not let the holder do the work. Does not list their classes by itself. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.ViewStudentContact',
    label: 'View Student Contact',
    description:
      'May see a linked student\'s contact on the parent contact page. Does not update. ' +
      'parent.UpdateStudentContact includes this view. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.UpdateStudentContact',
    label: 'Update Student Contact',
    description:
      'May update a linked student\'s contact. Includes parent.ViewStudentContact. ' +
      'Street address is stored in addresses (user_id). Email and phone stay on users. ' +
      'If that student can_login and the field already has a value, changing it needs people.EditUserData, not this cap. ' +
      'The platform owner\'s email and phone can only be changed by that person. ' +
      'Does not update this parent\'s own contact or another guardian. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.ViewGuardianContact',
    label: 'View Guardian Contact',
    description:
      'May see the other guardians linked to this parent\'s student on the parent contact page. Does not update. ' +
      'parent.UpdateGuardianContact includes this view. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.UpdateGuardianContact',
    label: 'Update Guardian Contact',
    description:
      'May update another guardian linked to this parent\'s student. Street address is stored in addresses (user_id). Email and phone stay on users. ' +
      'If that guardian can_login and the field already has a value, changing it needs people.EditUserData, not this cap. ' +
      'The platform owner\'s email and phone can only be changed by that person. ' +
      'Does not add or change a guardian relationship (parent.ManageStudentGuardianRelationships). ' +
      'Does not update this parent\'s own contact (people.UpdateOwnContact) or the student (parent.UpdateStudentContact). ' +
      'Does not update every person at the school (people.UpdateContact). Membership-only (GP/UGP).',
  },
  {
    id: 'parent.ManageStudentGuardianRelationships',
    label: 'Manage Student Guardian Relationships',
    description:
      'May add a guardian to a linked student in Acuity Learn, and update that guardian link: type, primary, and emergency. ' +
      'Guardian type only. Does not add a counselor or a sibling, does not remove a link, and does not change email, phone, or address. ' +
      'Does not come with people.PersonLinks. Membership-only (GP/UGP).',
  },
  {
    id: 'student.AddSelfGuardianRelationships',
    label: 'Add Self Guardian Relationships',
    description:
      'A student may add a guardian to themselves only. Guardian type only. ' +
      'Does not add a guardian to another student and does not remove a link. Membership-only (GP/UGP).',
  },
  {
    id: 'people.GrantContactPermissions',
    label: 'Grant Contact Permissions',
    description:
      'May open the gear on Edit relationship in Acuity Learn and set contact doors on that guardian\'s membership (UGP): ' +
      'parent.ViewStudentContact or parent.UpdateStudentContact, and parent.ViewGuardianContact or parent.UpdateGuardianContact. ' +
      'Update includes view, so the view door is not stored when update is on. ' +
      'Which schools this gear covers follows this grant\'s scope: this membership\'s school, scope.ListedGroups, or scope.Global. ' +
      'Does not view or update contact by itself. Membership-only (GP/UGP).',
  },
  {
    id: 'parent.MessageStudentsOffice',
    label: 'Message Students Office',
    description:
      'Start, read, and reply on office requests for schools this parent\'s linked students attend, including notes this parent already started. ' +
      'Replaces parent.ContactSchoolAdmin. An old grant of that id is rewritten to this key. ' +
      'Does not use messages.ReplyMessages. That cap answers as the office from the inbox. ' +
      'Not messages.SendMessages: that cap does not pick a school through a student. ' +
      'A parent with no linked students holds this and has no office to write until a student is linked. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'school.AppearAsSchool',
    label: 'Appear As School',
    description:
      'Marks a UIS group (organization or team) as a School in Acuity Learn. Grant on the ' +
      'durable group itself (grantee_type = groups) — not a groups_period and not a UGP. ' +
      'Periods and enrollments stay in UIS; this flag only decides which groups count as schools. ' +
      'Still requires platform_access for Acuity Learn on some membership before other product grants apply.',
  },
  {
    id: 'school.CreateSchools',
    label: 'Create Schools',
    description:
      'May create a new UIS organization (display_label School) or link an existing group by granting ' +
      'school.AppearAsSchool. Does not include edit or remove. Membership-only (GP/UGP).',
  },
  {
    id: 'school.EditSchools',
    label: 'Edit Schools',
    description:
      'May rename or update description of groups already marked Appear As School. Does not create or remove. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'school.RemoveSchools',
    label: 'Remove Schools',
    description:
      'May revoke school.AppearAsSchool so the group no longer appears as a school in Acuity Learn. ' +
      'The UIS group is not deleted. Membership-only (GP/UGP).',
  },
  {
    id: 'school.AddUsers',
    label: 'Add Users to Schools',
    description:
      'May add people onto an Acuity Learn school roster (creates/finds UIS user, role team, GP, and UGP). ' +
      'Does not create global platform users outside a school, and does not remove users. ' +
      'metadata.add_roles required when access is allow: admins | teachers | students | ' +
      'curriculum_developers | counselors | parents — which role badges this grantee may attach. ' +
      'parents does not add a roster row. It grants AcuityLearn access to a guardian already linked in SIS. ' +
      'Stamps the Role Defaults kit onto that membership as-is. Changing which pages and capabilities they receive needs school.OverrideAddUserPermissions (the gear on the role badge). ' +
      'Super Admin cannot be added (founder identity). Membership-only (GP/UGP).',
  },
  {
    id: 'school.OverrideAddUserPermissions',
    label: 'Override Add User Permissions',
    description:
      'May open the gear when adding a person in Acuity Learn and change which pages and capabilities that membership receives. ' +
      'Without it, school.AddUsers stamps the Role Defaults kit as-is. ' +
      'Does not grant Add Users — the holder still needs school.AddUsers. Does not open Role Defaults. Membership-only (GP/UGP).',
  },
  {
    id: 'school.EditGradeScale',
    label: 'Edit Grade Scale',
    description:
      'May see and change the letter scale for an Acuity Learn school. Every class at that school uses it. ' +
      'Does not come with school.EditSchools. Membership-only (GP/UGP).',
  },
  {
    id: 'school.EditSchoolCalendar',
    label: 'Edit School Calendar',
    description:
      'May see and change an Acuity Learn school’s holidays, days off, early release, and testing days. ' +
      'Does not come with school.EditSchools. Membership-only (GP/UGP).',
  },
  {
    id: 'messages.ReceiveMessages',
    label: 'Receive Messages',
    description:
      'Read the office inbox for schools in this membership’s scope. ' +
      'metadata.thread_kinds must include office when access is allow. ' +
      'Support is shown in Page Permissions Manager and is not stored yet. ' +
      'Does not start a thread (messages.SendMessages) and does not reply (messages.ReplyMessages). ' +
      'Does not come with school.EditSchools. Also defaults page_access.messages on when no explicit messages page row exists. ' +
      'A teacher uses this same Messages page when the cap is granted. Membership-only (GP/UGP).',
  },
  {
    id: 'messages.SendMessages',
    label: 'Send Messages',
    description:
      'Start a thread of a kind in metadata.thread_kinds. Office is a request to a school office. ' +
      'The sender can view the threads they started. It does not open the school office inbox. ' +
      'Which school they may address is a relationship, not this cap. ' +
      'A parent writing the office of a linked student\'s school uses parent.MessageStudentsOffice, not this cap. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'messages.ReplyMessages',
    label: 'Reply Messages',
    description:
      'Answer as the office, from the inbox, for each kind checked in metadata.thread_kinds. ' +
      'Office means reply on office posts this membership can already read. ' +
      'Support, once that kind is stored, means reply on support posts they can receive. It is not stored yet. ' +
      'The office inbox needs this on top of messages.ReceiveMessages. ' +
      'A parent continuing a note they started uses parent.MessageStudentsOffice, not this cap. Membership-only (GP/UGP).',
  },
  {
    id: 'dm.Send',
    label: 'Send Direct Message',
    description:
      'Start a direct message. The sender can read the threads they started. Continuing one needs dm.Reply. ' +
      'Does not open the office inbox and is not messages.SendMessages. ' +
      'If the holder also has people.SearchPeople, the form can search the directory. Without it, they type an email. This cap does not grant that search. ' +
      'Not the parent-to-teacher door. That is dm.SendStudentTeacher. ' +
      'Person links on a student are dm.SendStudentRelationship. Membership-only (GP/UGP).',
  },
  {
    id: 'dm.Reply',
    label: 'Reply to Direct Message',
    description:
      'Reply on a direct message the holder can already see. Does not start one. ' +
      'Grant this without dm.Send when someone should answer a message and not open a new one. ' +
      'A thread opened with dm.SendStudentTeacher or dm.SendStudentRelationship continues on that cap, not this one. Membership-only (GP/UGP).',
  },
  {
    id: 'dm.SendStudentTeacher',
    label: 'Send Student Teacher',
    description:
      'A linked guardian or counselor may start, read, and reply on a direct message to a teacher of that student. ' +
      'The names are instructors on that student\'s course enrollments whose kit has student chat. ' +
      'No directory search and no email box. Does not use dm.Send or dm.Reply. ' +
      'A person with no linked student has no names to pick. Membership-only (GP/UGP).',
  },
  {
    id: 'dm.SendStudentRelationship',
    label: 'Send Student Relationship',
    description:
      'Start, read, and reply on a direct message to a person linked to a student. ' +
      'metadata.relationship_types is required when access is allow: guardian, sibling, teacher_of_record, counselor, supervisor. ' +
      'Omitted or empty means none. The student is parent_id. The other person is child_id. ' +
      'No directory search and no email box. Does not use dm.Send or dm.Reply. ' +
      'Does not message the student. Teachers on a course enrollment are dm.SendStudentTeacher. Membership-only (GP/UGP).',
  },
  {
    id: 'dm.ViewOthersInboxes',
    label: 'View Others Inboxes',
    description:
      'May read other people’s direct-message inboxes for schools in this membership’s scope. ' +
      'metadata.inbox_roles is required when access is allow: super_admins, admins, teachers, students, ' +
      'curriculum_developers, counselors, parents. Omitted or empty means none. ' +
      'This is not the holder’s own Messages. Threads they started are dm.Send. Threads they continue are dm.Reply. ' +
      'A parent reading a linked student’s messages is not this cap. Does not send or reply. ' +
      'Also defaults page_access.messages on when no explicit messages page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'people.ViewPersonLinks',
    label: 'View Person Links',
    description:
      'May see who is linked to a student. Does not allow adding or removing a link. Membership-only (GP/UGP).',
  },
  {
    id: 'people.SearchPeople',
    label: 'Search People',
    description:
      'May search the directory and link an existing person as a guardian. ' +
      'Create new does not need this and does not attach to an email that already exists. ' +
      'Does not update contact, grant a sign-in, or search counselors. Counselor search stays people.PersonLinks with counselors. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'people.StudentStaffChat',
    label: 'Student Staff Chat',
    description:
      'The one student-and-staff thread on a student membership. The student can see this room. ' +
      'This avoids a person link for every student. It is scope-wide: every student at a school in this membership\'s scope, the same idea as course.TeachWithoutEnrollment, which opens classes in scope without a course_enrollments row. ' +
      'A linked person opens the same room with parent.ViewStudentChat. ' +
      'Also opens your own private notes on that student. Does not open the staff-only room. Membership-only (GP/UGP).',
  },
  {
    id: 'people.StaffStudentChat',
    label: 'Staff About Student',
    description:
      'The one staff-only thread on a student membership. The student cannot see this room. ' +
      'This avoids a person link for every student. It is scope-wide: every student at a school in this membership\'s scope, the same idea as course.TeachWithoutEnrollment, which opens classes in scope without a course_enrollments row. ' +
      'Also opens your own private notes on that student. Does not open the student-visible room. Membership-only (GP/UGP).',
  },
  {
    id: 'people.PersonLinks',
    label: 'Person Links',
    description:
      'May link or unlink person links on a student at an Acuity Learn school. ' +
      'metadata.link_types required when access is allow: guardians | counselors | siblings. ' +
      'Parents are a guardian type, not their own link type. A stored parents value still counts as guardians. ' +
      'Does not come with school.AddUsers. Membership-only (GP/UGP).',
  },
  {
    id: 'people.UpdateOwnContact',
    label: 'Update Own Contact',
    description:
      'May update the signed-in person\'s own contact only. Street address is stored in addresses (user_id). Email and phone stay on users. ' +
      'If can_login is true and that email or phone already has a value, changing it needs people.EditUserData, not this cap. ' +
      'The platform owner needs that cap too, and nobody else can change that person\'s email or phone. ' +
      'Does not update anyone else. Membership-only (GP/UGP).',
  },
  {
    id: 'people.EditUserData',
    label: 'Edit User Data',
    description:
      'Overwrite a sign-in email or phone that already has a value. The person still needs a contact update door. ' +
      'Filling an empty email or phone does not need this. ' +
      'Editing someone else is allowed only when every membership they have is inside this school scope. ' +
      'scope.Global covers every school and still does not cover a System seat. A context outside that scope needs people.EditUserDataMaster. ' +
      'Editing yourself skips that scope check. ' +
      'The platform owner needs this cap, or people.EditUserDataMaster, to change their own email or phone. Nobody else can. ' +
      'Does not grant a contact update by itself. Membership-only (GP/UGP).',
  },
  {
    id: 'people.EditUserDataMaster',
    label: 'Edit User Data Master',
    description:
      'Overwrite a sign-in email or phone that already has a value when that person has a membership outside this school scope, including a System seat. ' +
      'Includes the people.EditUserData unlock. The person still needs a contact update door. ' +
      'Does not let anyone except the platform owner change the platform owner’s email or phone. ' +
      'scope.Global does not grant this. Does not grant a contact update by itself. Membership-only (GP/UGP).',
  },
  {
    id: 'people.ViewContact',
    label: 'View Contact Information',
    description:
      'May see contact for people in this membership\'s school scope, from the people view. The default is that school. ' +
      'scope.ListedGroups limits it to the listed schools. scope.Global expands it to every school. ' +
      'Does not update. people.UpdateContact includes this view. ' +
      'Also shows a linked student on the parent contact page. A parent normally uses parent.ViewStudentContact. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'people.UpdateContact',
    label: 'Update Contact Information',
    description:
      'May update contact for people in this membership\'s school scope, from the people view. Includes people.ViewContact. ' +
      'The default is that school. scope.ListedGroups limits it to the listed schools. scope.Global expands it to every school. ' +
      'Street address is stored in addresses (user_id). Email and phone stay on users. ' +
      'If that person can_login and the field already has a value, changing it needs people.EditUserData, not this cap. ' +
      'The platform owner\'s email and phone can only be changed by that person. ' +
      'Also edits a linked student on the parent contact page. A parent normally uses parent.UpdateStudentContact. ' +
      'Does not replace Update Own Contact or Update Guardian Contact. Membership-only (GP/UGP).',
  },
  {
    id: 'school.ViewUsers',
    label: 'View People',
    description:
      'May see people on an Acuity Learn school roster. metadata.view_roles required when access is allow: ' +
      'super_admins | admins | teachers | students | curriculum_developers | counselors — which role slices ' +
      'are visible. Does not include add. Also defaults page_access.schools on when no explicit schools page ' +
      'row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ViewCourses',
    label: 'View Courses',
    description:
      'May see the courses a school already holds (its copies) in Acuity Learn. With scope.Global, that is every school\'s copies. Does not open the master ' +
      'catalog at /courses — that is course.ViewMasterCatalog (or CreateMasterCourses / EditMasterCourses / ProposeMasterCourses / ReviewCatalogProposals / UpdateCatalogAccess / RemoveMasterCourses) — and does not default page_access.courses. Also defaults page_access.course-builder ' +
      'on when no explicit course-builder page row exists. Does not grant live edit or propose — those need ' +
      'course.EditCourses, course.TeachEnrolled plus an instructor enrollment on that copy, or course.CreateCourses on this person\'s own unowned non-master shelf draft. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ViewMasterCatalog',
    label: 'View Master Catalog',
    description:
      'May open the Catalog slice on Courses (is_master templates). Browse only. ' +
      'Does not grant give-out (course.AssignToSchools), request a school copy (course.ProposeAssignToSchool), author templates (course.CreateMasterCourses), or change catalog locks (course.UpdateCatalogAccess). ' +
      'Does not grant live edit or propose on masters — that is course.EditMasterCourses / course.ProposeMasterCourses, not course.EditCourses and not this cap. ' +
      'Does not show school copies — that is course.ViewCourses. Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ViewOthersCourses',
    label: 'View Others\' Courses',
    description:
      'May see other people\'s unowned, non-template courses (their My courses shelves) in the holder\'s school scope. ' +
      'School-scoped: drafts stamped to a membership under a school in scope. Global: every school\'s shelves plus authors with no school UGP. ' +
      'Does not grant View Courses (school-held copies), Catalog give-out, or live edit. Names on those cards still need school.ViewUsers. ' +
      'Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.CreateCourses',
    label: 'Create Courses',
    description:
      'May author a new unowned, non-master course on My courses (a shelf draft). Also opens My courses. ' +
      'Write door for that draft only while it has no owner_school_id and is not is_master, and only if this person authored it (created_by). Title, description, and the outline are live-edit without course.EditCourses. ' +
      'Does not write anyone else\'s shelf. Does not write a school copy (that is course.EditCourses, or course.TeachEnrolled plus instructor enrollment). Does not write a catalog template (is_master) — that is course.EditMasterCourses / course.ProposeMasterCourses. ' +
      'Once the draft is placed on a school (course.PlaceOnSchool) or tagged master (course.CreateMasterCourses), this cap stops writing that row. ' +
      'Does not set is_master — including for global users. Founding templates need course.CreateMasterCourses. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists, so the holder can reach /courses/:id/build without course.EditCourses. Membership-only (GP/UGP).',
  },
  {
    id: 'course.CreateMasterCourses',
    label: 'Create Master Courses',
    description:
      'May author a catalog template, instantly move an unattached course into or out of the catalog (is_master), and apply or reject catalog-upgrade requests. ' +
      'Upgrade is for My shelf drafts; downgrade returns a template to the shelf. School-owned copies are not tagged in place — applying a Propose to Catalog request clones a new catalog template and leaves the school copy. ' +
      'Applying a course.ProposeToCatalog request is this cap — not course.ReviewProposals and not course.ReviewCatalogProposals. Promotion is not content-proposal review. ' +
      'At create or first promote, the holder sets the initial Access / Copy / Mod lock. Later changes need course.UpdateCatalogAccess (or course.EditDespiteModLock if already mod-locked). ' +
      'Independent of scope.Global. Also opens the master Catalog. Does not grant live edit or propose on existing masters — that is course.EditMasterCourses / course.ProposeMasterCourses. After a template is tagged, this cap stops writing that row. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.EditMasterCourses',
    label: 'Edit Master Courses',
    description:
      'May live-edit existing catalog templates (is_master) when can_edit is on. Catalog analog of course.EditCourses — it does not write school copies. ' +
      'Does not inherit propose: a propose-only master (can_edit off, can_propose on) needs course.ProposeMasterCourses. ' +
      'Does not found templates (course.CreateMasterCourses), change Access / Copy / Mod lock (course.UpdateCatalogAccess), or bypass mod_lock (course.EditDespiteModLock). ' +
      'course.EditCourses, course.ViewMasterCatalog, course.CreateMasterCourses, and scope.Global do not grant this. ' +
      'Implies course.ReviewCatalogProposals on that master only while it is live-editable. Also opens the master Catalog. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ProposeMasterCourses',
    label: 'Propose Master Courses',
    description:
      'May submit content proposals on an existing catalog template when can_edit is off and can_propose is on. Never live-edits. Catalog analog of Teach Enrolled propose — scoped to masters, not every school copy (there is no course.ProposeCourses). ' +
      'Does not apply those proposals (course.ReviewCatalogProposals / live EditMasterCourses). Does not found templates (course.CreateMasterCourses) and does not request that a non-catalog course join the catalog (course.ProposeToCatalog). ' +
      'course.EditCourses and course.ViewMasterCatalog do not grant this. Also opens the master Catalog. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ProposeToCatalog',
    label: 'Propose to Catalog',
    description:
      'May request that any non-catalog course be added to the catalog. Unattached shelf drafts are tagged in place (is_master). School-owned copies stay on the school; applying clones a new catalog template from them. Stores a special pending row on course_proposals (action promote). Does not tag the course itself. ' +
      'Apply / reject is course.CreateMasterCourses. course.ReviewProposals and course.ReviewCatalogProposals cannot apply these — those review content proposals on copies or masters, not promotion requests. ' +
      'The proposer may withdraw their own pending request. Does not grant Create Master Courses, Edit Courses, or Update Catalog Access. ' +
      'Also defaults page_access.courses on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.UpdateCatalogAccess',
    label: 'Update Catalog Access',
    description:
      'May change Access / Copy / Mod lock on an existing catalog template after create. Catalog analog of course.SetSchoolCopyEdit. ' +
      'Does not grant live edit (course.EditMasterCourses) and does not tag is_master (course.CreateMasterCourses). ' +
      'Create Master Courses still sets the initial locks at create or first promote. If the template is already mod-locked, only course.EditDespiteModLock can change these flags. ' +
      'Also opens the master Catalog. Also defaults page_access.courses on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.EditCourses',
    label: 'Edit Courses',
    description:
      'Writer door for every school-owned course copy in the holder\'s school scope. Does not write catalog templates — that is course.EditMasterCourses (live) or course.ProposeMasterCourses (propose). Seeing Catalog (course.ViewMasterCatalog) or holding scope.Global / CreateMasterCourses does not extend this cap onto masters. ' +
      'Not a lock, and not a propose-only cap. The row decides the mode: can_edit on → live edit; can_edit off + can_propose on → propose; both off or mod_lock → nothing ' +
      '(unless course.EditDespiteModLock). ' +
      'There is no UIS ProposeCourses for school copies — this cap inherits propose when the row is propose-only. Propose on a master is course.ProposeMasterCourses, not this inherit. ' +
      'ViewCourses / course-builder page is not enough. Teachers do not get this cap; they write school copies via course.TeachEnrolled plus instructor enrollment, and their own unowned shelf drafts via course.CreateCourses. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'course.ReviewProposals',
    label: 'Review Course Proposals',
    description:
      'May apply or reject content proposals on a non-master course (a school copy or shelf draft) the holder can see, including when can_edit is off. Still blocked by mod_lock unless course.EditDespiteModLock. ' +
      'Does not review catalog templates — that is course.ReviewCatalogProposals. ' +
      'Does not apply catalog-upgrade (promote) requests — that is course.CreateMasterCourses. Does not grant live mutate, Teach Enrolled, or Edit Courses. ' +
      'Edit Courses already reviews a live-editable school copy. Grant this alone on propose-only copies. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'course.ReviewCatalogProposals',
    label: 'Review Catalog Proposals',
    description:
      'May apply or reject content proposals on a catalog template (proposed edits to an existing master), including when can_edit is off. Still blocked by mod_lock unless course.EditDespiteModLock. ' +
      'Does not review school copies or shelf drafts — that is course.ReviewProposals. ' +
      'Does not apply Propose to Catalog promotion requests — that is course.CreateMasterCourses. ' +
      'course.ReviewProposals, course.ProposeMasterCourses, and course.ViewMasterCatalog do not grant this. ' +
      'Edit Master Courses already reviews a live-editable master. Grant this alone on propose-only templates. Also opens the master Catalog. ' +
      'Also defaults page_access.courses and page_access.course-builder on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.TeachEnrolled',
    label: 'Teach Enrolled Courses',
    description:
      'May teach courses they are enrolled on as instructor — not every copy in scope (that is course.EditCourses / course.TeachWithoutEnrollment) and not a personal My courses draft (that is course.CreateCourses). ' +
      'Without an instructor enrollment this cap does nothing on a course. Deny it on a teacher GP/UGP and every enrolled course for those people stops mutating. ' +
      'metadata.instructor_kit is a ceiling, not the per-course kit. Defaults all four on when omitted: ' +
      'propose_activities, edit_activities, grade_activities, override_activities. Which activities those apply to is course_enrollments.permissions / instructor_defaults — not configured here. ' +
      'Learn can only narrow these verbs, never widen past UIS. Papers, override, and student chat on an enrolled copy follow this kit plus the enrollment JSON. ' +
      'There is no UIS ProposeCourses capability; propose for teachers is this checkbox, then the copy\'s can_propose lock. ' +
      'If the UIS kit and the enrollment allow edit or propose, the Build hammer appears on that copy — course.CreateCourses and course.EditCourses are not required. ' +
      'Also defaults page_access.teach and page_access.course-builder on when no explicit row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.TeachWithoutEnrollment',
    label: 'Teach Without Enrollment',
    description:
      'May open Teach for school-owned copies in membership scope without an instructor enrollment on those copies. Does not list the holder as a teacher. Does not grant propose or live-edit (that is course.EditCourses). Does not need course.TeachEnrolled. ' +
      'metadata.supervisor_kit is this cap\'s own kit. Defaults all on when omitted: grade_activities (view and mark), view_activities (view without mark), override_activities, student_progress_chat, staff_progress_chat. Empty list is all off. ' +
      'If the holder is also enrolled with Teach Enrolled on a copy, the enrollment kit wins on that copy. ' +
      'Grant this to school admins who must look at roster and submissions without adding themselves as instructors. ' +
      'Also defaults page_access.teach on when no explicit Teach page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.PostAnnouncements',
    label: 'Post Class Announcements',
    description:
      'May post a short note to students enrolled in a school-owned course copy (student Today → Teacher updates). ' +
      'Staff compose from Teach, Courses (school copies), or Course Builder. ' +
      'Still needs a live instructor enrollment on that copy, or course.EditCourses (school admin). ' +
      'Does not post on catalog templates. Does not default on with Teach Enrolled or Edit Courses — grant course.PostAnnouncements. Membership-only (GP/UGP).',
  },
  {
    id: 'course.EditDespiteModLock',
    label: 'Edit Despite Mod Lock',
    description:
      'May live-edit a school copy or a master template even when mod_lock is on (and when can_edit is off). Super-admin only in Learn templates. ' +
      'On a locked master, this is also the only cap that can change Access / Copy / Mod lock (including turning mod_lock off) — course.UpdateCatalogAccess is not enough while frozen. Still needs Catalog access to see masters. ' +
      'Does not grant Edit Courses or Edit Master Courses by itself. Does not default extra pages. Membership-only (GP/UGP).',
  },
  {
    id: 'course.OverrideActivityDefaults',
    label: 'Override Activity Defaults',
    description:
      'May open the activity settings gear in the course builder and change fields that are off by default for that type ' +
      '(time limit on a discussion, due date on a reading, require-previous, and so on). ' +
      'Does not grant Edit Courses — the holder still needs that to save. ' +
      'Default-visible fields (due and points on an exam) stay editable with Edit Courses alone. ' +
      'Does not default extra pages. Membership-only (GP/UGP).',
  },
  {
    id: 'course.UseBuilderAssistant',
    label: 'Use Course Builder Assistant',
    description:
      'May use the staff AI assistant in Course Builder (library-first authoring help, web fallback, can draft and write items/questions). ' +
      'Does not grant Edit Courses, Edit Master Courses, or Teach Enrolled — writes still need those doors and the copy locks. ' +
      'Does not restore prior chat or send earlier visits as model context — that is course.BuilderAssistantHistory. ' +
      'Does not default extra pages. Hidden unless this cap is allowed. Membership-only (GP/UGP).',
  },
  {
    id: 'course.BuilderAssistantHistory',
    label: 'Builder Assistant History',
    description:
      'May restore this person\'s Course Builder assistant thread for a course (same user + course) and send prior turns as model context. ' +
      'Without it, each visit is a fresh thread in the panel and the model is not given earlier messages. ' +
      'Chats are still saved for admin and token tracking either way. ' +
      'Does not grant Use Course Builder Assistant — that door is separate. Does not default extra pages. Membership-only (GP/UGP).',
  },
  {
    id: 'course.CourseBreaker',
    label: 'Course Breaker',
    description:
      'May use the hidden irreversible outline deletion tool in Course Builder. The control is not in the nav and does not appear unless the builder URL has CourseBreaker=true (or allow_deletion=true). ' +
      'Permanently removes selected units, lessons, activities, and opted-in questions, plus opted-in learner work on those outline rows. ' +
      'Does not grant Edit Courses. Does not delete the whole course — that needs course.RemoveCourses (school copies and shelf drafts) or course.RemoveMasterCourses (catalog templates), and is refused while people are assigned, learner work exists, or proposals are pending. Does not default extra pages. Do not attach this by default — grant by word of mouth. Membership-only (GP/UGP).',
  },
  {
    id: 'course.RemoveCourses',
    label: 'Remove Courses',
    description:
      'May permanently delete a non-master course: a school-owned copy or an unowned shelf draft. ' +
      'Does not delete catalog templates — that is course.RemoveMasterCourses. ' +
      'Refuses while anyone is still assigned, while learner work exists, or while proposals are pending. Does not wipe student records. ' +
      'Does not withdraw a copy from a school (that is course.AssignToSchools restore/withdraw). ' +
      'Does not open the master Catalog. Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.RemoveMasterCourses',
    label: 'Remove Master Courses',
    description:
      'May permanently delete a catalog template (is_master). School copies that were given out from it stay on those schools (their source link is cleared). ' +
      'Refuses while proposals on the template are pending. Masters are not enrolled, so roster checks do not apply. ' +
      'Does not delete school copies or shelf drafts — that is course.RemoveCourses. ' +
      'course.RemoveCourses, course.CreateMasterCourses, and course.ViewMasterCatalog do not grant this. Also opens the master Catalog. ' +
      'Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.AssignToSchools',
    label: 'Assign Courses To Schools',
    description:
      'May copy a master / catalog template onto a school in scope (the school gets its own course row). That is all this cap assigns — not a My courses shelf draft and not a course the school already holds. ' +
      'Also withdraw that school copy from By School, and restore it. ' +
      'Does not flip locks later — that is course.SetSchoolCopyEdit. ' +
      'Initial locks at give-out require SetSchoolCopyEdit (Can edit / Can propose / View only, plus copy). ' +
      'Without that cap the copy lands propose-only and not copyable. ' +
      'Does not place a non-master shelved course onto a school — that is course.PlaceOnSchool (pin the draft, or copy so the shelf keeps the original). ' +
      'Does not open the master Catalog — that is course.ViewMasterCatalog (or CreateMasterCourses / EditMasterCourses / ProposeMasterCourses / ReviewCatalogProposals / UpdateCatalogAccess / RemoveMasterCourses). Give-out is limited to schools in the holder\'s scope. ' +
      'Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ProposeAssignToSchool',
    label: 'Propose Assign To School',
    description:
      'May request that a catalog / master course be copied onto a school in the holder\'s scope. Stores a pending course_proposals row (action assign). Does not copy immediately — that is course.AssignToSchools. ' +
      'Apply / reject is course.AssignToSchools. course.ReviewProposals cannot apply these. The proposer may withdraw their own pending request. ' +
      'Does not open Catalog by itself — that is course.ViewMasterCatalog (or CreateMasterCourses / EditMasterCourses / ProposeMasterCourses / ReviewCatalogProposals / UpdateCatalogAccess / RemoveMasterCourses). ' +
      'Does not grant Assign to Schools, Create Master Courses, or Edit Courses. Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.PlaceOnSchool',
    label: 'Place On School',
    description:
      'May put a non-master course on a school in scope: a My courses / Others shelf draft (unowned, not is_master). ' +
      'Give it to the school by pinning (same row, owner_school_id set) or copy it so the shelf keeps the original and the school gets a new row. ' +
      'Does not copy a master / catalog template onto a school — that is course.AssignToSchools. Does not flip is_master. ' +
      'Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.CopyCourses',
    label: 'Copy Courses',
    description:
      'May duplicate a course the holder may access when that row has can_copy. The new copy stays on the same school, ' +
      'inherits can_edit / can_copy / can_propose / mod_lock / instructor_defaults (never more permissive), and is enrolled separately (course.ManageEnrollments). ' +
      'Does not copy a master onto another school — that is course.AssignToSchools. ' +
      'Also defaults page_access.courses on when no explicit courses page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'course.SetSchoolCopyEdit',
    label: 'Set School Copy Access',
    description:
      'May flip can_edit, can_copy, can_propose, and mod_lock on a school-owned copy. These are copy locks, not person grants. ' +
      'Does not flip those flags on a master / catalog template — that is course.UpdateCatalogAccess (and course.EditDespiteModLock if the master is already mod-locked). ' +
      'They apply only to writers: course.EditCourses (every copy in scope) or course.TeachEnrolled plus an instructor enrollment (that copy). Masters use course.EditMasterCourses / course.ProposeMasterCourses and course.UpdateCatalogAccess, not this cap. ' +
      'ViewCourses does not use them. can_edit on: those writers live-edit (the stronger door; they do not also need can_propose). ' +
      'can_edit off + can_propose on: those same writers propose — EditCourses inherits propose on school copies; teachers need Teach Enrolled propose_activities. Do not grant a UIS ProposeCourses cap (that would hit every course). Propose on a master is course.ProposeMasterCourses. ' +
      'To deny every enrolled course for a teacher, deny course.TeachEnrolled on their GP/UGP. To deny one course, turn off their enrollment kit, not a school-wide UIS cap. ' +
      'Both off: frozen. mod_lock: hard freeze unless course.EditDespiteModLock. ' +
      'can_copy false: CopyCourses cannot duplicate. Does not copy, enroll, or mutate content. Membership-only (GP/UGP).',
  },
  {
    id: 'course.ManageEnrollments',
    label: 'Manage Course Enrollments',
    description:
      'May enroll or revoke students (learners) and teachers (instructors) on a school-owned course copy (UIS UGP → course). ' +
      'An instructor may live-edit, propose, grade, or override attempts on that copy only when they also hold course.TeachEnrolled and the matching enrollment kit verb — same inherit as course.EditCourses, but only this course. Student chat is the enrollment flag (omitted is off). ' +
      'Does not grant Edit Courses, Teach Enrolled, or Assign To Schools. Listing still needs View Courses; picking from the school roster needs View People. ' +
      'Membership-only (GP/UGP).',
  },
  {
    id: 'learning.ViewOwnEnrollments',
    label: 'View Own Enrollments',
    description:
      'May see the courses the holder is enrolled in and read their published content (Today, My Learning, the Learning Path). ' +
      'Unpublished units, lessons, and activities stay hidden. Does not grant course.ViewCourses, and does not show another person’s enrollments or progress. ' +
      'Does not open the student app by itself — that defaults from role.IsStudent. Membership-only (GP/UGP).',
  },
  {
    id: 'learning.OpenActivities',
    label: 'Open Activities',
    description:
      'May open an activity in an enrolled course and have that opening recorded. Completes items with nothing to submit (reading, video, slides, key terms). ' +
      'Does not submit answers, does not score, and does not reach a course the holder is not enrolled in. Membership-only (GP/UGP).',
  },
  {
    id: 'learning.SubmitWork',
    label: 'Submit Work',
    description:
      'May submit answers for a graded activity (homework, assessment, exam) in an enrolled course. Auto-gradeable question types are scored on the server; ' +
      'short answer is captured and left for a teacher. Does not edit a submission once scored, and does not grade anyone else’s work. Membership-only (GP/UGP).',
  },
  {
    id: 'learning.ReplyOnProgress',
    label: 'Reply On Progress',
    description:
      'May post a message on the public work thread for the holder\'s own activity progress, when the activity (and any per-learner override) also allow replies. ' +
      'Does not grant staff chat, personal notes for someone else, or posting on another student\'s paper. Viewing the timeline is not this cap. ' +
      'Teachers do not need this to write on a public thread. Membership-only (GP/UGP).',
  },
  {
    id: 'library.ViewLibrary',
    label: 'View Library',
    description:
      'May see published Library sources in scope, plus own drafts. Proposed rows stay hidden unless the holder proposed them or also has library.ReviewSources. ' +
      'Other people\'s sources in school scope need library.ViewOthersSources. ' +
      'Does not add, edit, or publish. Also defaults page_access.library on when no explicit library page row exists. ' +
      'Super Admin does not imply this. Membership-only (GP/UGP).',
  },
  {
    id: 'library.ViewOthersSources',
    label: 'View Others\' Sources',
    description:
      'May see other people\'s Library sources (their My sources shelves) in the holder\'s school scope. ' +
      'School-scoped: rows stamped to a school in scope. Global: every school\'s shelves plus authors with no school stamp (Platform). ' +
      'Does not grant View Library, Add Sources, Edit Sources, or live write. Names on those cards still need school.ViewUsers. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.AddSources',
    label: 'Add Library Sources',
    description:
      'May author a new Library source as a draft (upload or URL). Not a proposal — that is library.ProposeSources. ' +
      'The adder can keep editing that draft while it is theirs and still unpublished. ' +
      'Does not publish (library.EditSources) and does not write anyone else\'s sources. ' +
      'library.EditSources and library.ViewLibrary do not grant this. Same metadata.source_kit as Edit Sources ' +
      '(kinds, replace_file vs edit_analysis, size). Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.EditSources',
    label: 'Edit Library Sources',
    description:
      'May live-edit existing Library sources in scope (not course outline rows), including publish / unpublish. ' +
      'Does not create a new row — that is library.AddSources (draft) or library.ProposeSources (proposed). ' +
      'metadata.source_kit is the ceiling (defaults all on when omitted): document | image | audio | video | url, ' +
      'replace_file (swap the file or URL), edit_analysis (tags / taxonomy / transcript without replacing the file), ' +
      'max_12mb | max_80mb | max_200mb (largest checked is the upload cap). Does not imply Add Sources or Review Sources. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.ProposeSources',
    label: 'Propose Library Sources',
    description:
      'May add or change a Library source as status = proposed. Same metadata.source_kit as Add / Edit Sources ' +
      '(kinds, replace_file vs edit_analysis, size). Does not create a live draft — that is library.AddSources. library.AddSources does not grant this. ' +
      'The proposer can keep editing that row. ' +
      'Does not publish. Apply / reject needs library.ReviewSources. Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.ReviewSources',
    label: 'Review Library Sources',
    description:
      'May apply or reject proposed Library sources. Does not grant live Edit Sources. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.RemoveSources',
    label: 'Remove Library Sources',
    description:
      'May archive a published Library source, or delete a draft / rejected row. Prefer archive when chunks were served. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.AnalyzeSources',
    label: 'Analyze Library Sources',
    description:
      'May run AI analysis on Library add / re-analyze (extract + Bedrock tags, summary, taxonomy, snapshot). ' +
      'Does not grant add or edit — those stay library.AddSources / EditSources / ProposeSources plus source_kit.edit_analysis. ' +
      'Without this cap Learn hides Skip AI analysis and forces skip; the holder can still type transcript, tags, and taxonomy. ' +
      'Audio / video stay skipped until Transcribe is wired, even with this cap. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'library.ManageApprovedCatalogs',
    label: 'Manage Approved Catalogs',
    description:
      'May turn approved catalog connectors (Library of Congress, later others) on or off. Does not grant Edit Sources. ' +
      'Also defaults page_access.library on when no explicit library page row exists. Membership-only (GP/UGP).',
  },
  {
    id: 'taxonomy.CreateTaxonomy',
    label: 'Create Taxonomy',
    description:
      'May create UIS taxonomy Core subjects and Concentrations from Acuity Learn (course picker), without opening SIS. ' +
      'Does not include edit or remove. Independent of SIS taxonomy.CreateCoreSubjects / CreateConcentration and of ' +
      'LearnPath taxonomy.CreateTaxonomy — same table, product-scoped grant. Membership-only (GP/UGP).',
  },
  {
    id: 'taxonomy.EditTaxonomy',
    label: 'Edit Taxonomy',
    description:
      'May edit UIS taxonomy Core subjects and Concentrations from Acuity Learn. Does not create or remove. ' +
      'Independent of SIS taxonomy.EditCoreSubjects / EditConcentrations. Membership-only (GP/UGP).',
  },
  {
    id: 'taxonomy.RemoveTaxonomy',
    label: 'Remove Taxonomy',
    description:
      'May delete UIS taxonomy Core subjects and Concentrations from Acuity Learn. The UIS row is removed (SIS forms ' +
      'and other products that referenced it lose the link). Does not create or edit. Membership-only (GP/UGP).',
  },
  {
    id: 'scope.Global',
    label: 'Scope Global',
    description:
      'Platform-wide school visibility in Acuity Learn: all groups marked Appear As School. ' +
      'Grant this capability explicitly — Super Admin is only a label and does not imply Global. ' +
      'Expands course.ViewCourses to every school\'s copies. Does not open the master Catalog and does not write masters. ' +
      'Mutually exclusive with scope.ListedGroups on the same GP/UGP — Global wins at runtime. ' +
      'Membership-only (GP/UGP). Does not grant role badges or school create/edit/remove by itself.',
  },
  {
    id: 'scope.ListedGroups',
    label: 'Scope Listed Groups',
    description:
      'Selected-school visibility only. Set rules.scope_group_ids (preferred) or ' +
      'scope_groups_periods_ids to the school groups this admin may see. Important: every other ' +
      'capability on this membership applies uniformly to all listed schools — you cannot set ' +
      'different permissions per listed school here. For different access at different schools, use ' +
      'a separate UGP (membership in that school’s group) instead. Mutually exclusive with ' +
      'scope.Global on the same GP/UGP. Membership-only (GP/UGP).',
  },
].map((entry) => ({
  id: entry.id,
  label: entry.label || humanizePermissionKey(entry.id),
  ...(entry.description ? { description: entry.description } : {}),
}));

const ACUITYLEARN_PAGES = [
  {
    id: 'home',
    label: 'Home',
    description:
      'Acuity Learn home. Defaults on with platform_access when no explicit page_access.home row exists.',
  },
  {
    id: 'profile',
    label: 'Profile',
    description:
      'Acuity Learn profile for the current UIS session. Defaults on with platform_access when no ' +
      'explicit page_access.profile row exists.',
  },
  {
    id: 'schools',
    label: 'Schools',
    description:
      'List and open Acuity Learn schools (groups marked Appear As School). Defaults on when ' +
      'school.CreateSchools, school.EditSchools, school.RemoveSchools, school.AddUsers, school.OverrideAddUserPermissions, or school.ViewUsers is allowed ' +
      'and no explicit page_access.schools deny exists. Create / edit / remove / add-user / view-people still need ' +
      'their own capabilities. Grade scale (school.EditGradeScale), school calendar (school.EditSchoolCalendar), and linking a parent (people.PersonLinks) are separate and do not open this page by themselves.',
  },
  {
    id: 'messages',
    label: 'Messages',
    description:
      'Staff inbox (/messages). Office is the school office. Direct is one-to-one messages when dm.Send or dm.Reply is granted. Defaults on with messages.ReceiveMessages, dm.Send, dm.Reply, or dm.ViewOthersInboxes ' +
      'when no explicit page_access.messages deny exists. Not a notification and not email. ' +
      'Old page_access.parent-messages grants still match this key. Membership-only (GP/UGP).',
  },
  {
    id: 'teach',
    label: 'Teach',
    description:
      'Teacher home (/teach). Lists school-owned course copies this membership is enrolled on as instructor, and — with course.TeachWithoutEnrollment — other school copies in scope without adding them as a teacher. ' +
      'Click a card to open that course (roster + submissions when allowed). Hover shows Propose / Edit / Grade for the verbs this membership holds on that copy. ' +
      'Announce hangs off the cards. Catalog templates never appear here. ' +
      'Defaults on with course.TeachEnrolled or course.TeachWithoutEnrollment when no explicit page_access.teach deny exists. ' +
      'Not the Courses catalog (that is page_access.courses). Old page_access.classes grants still match this key. Membership-only (GP/UGP).',
  },
  {
    id: 'courses',
    label: 'Courses',
    description:
      'Acuity Learn course catalog (/courses) — master courses and school assignments. Defaults on when ' +
      'course.CreateCourses, course.CreateMasterCourses, course.EditMasterCourses, course.ProposeMasterCourses, course.ProposeToCatalog, course.ViewMasterCatalog, course.ProposeAssignToSchool, course.UpdateCatalogAccess, course.EditCourses, course.ReviewProposals, course.ReviewCatalogProposals, course.RemoveCourses, course.RemoveMasterCourses, ' +
      'course.AssignToSchools, course.PlaceOnSchool, course.CopyCourses, course.SetSchoolCopyEdit, or course.ViewOthersCourses is allowed and no explicit ' +
      'page_access.courses deny exists. ' +
      'course.ViewCourses does not default this page on (school copies only). Create / edit / remove / ' +
      'assign / create-master still need their own capabilities. Does not open the course builder — that ' +
      'needs page_access.course-builder.',
  },
  {
    id: 'course-builder',
    label: 'Course Builder',
    description:
      'Acuity Learn course outline builder (/courses/:id/build) — units, lessons, items, and questions. ' +
      'Defaults on when course.ViewCourses, course.CreateCourses, course.CreateMasterCourses, course.EditMasterCourses, course.ProposeMasterCourses, course.EditCourses, course.ReviewProposals, course.ReviewCatalogProposals, or course.TeachEnrolled ' +
      'is allowed and no explicit page_access.course-builder deny exists. Opening is not writing. Live edit and propose need one of: ' +
      'course.EditCourses (school copies in scope); course.EditMasterCourses / course.ProposeMasterCourses (catalog templates); course.TeachEnrolled plus an instructor enrollment on that copy, then the row locks; or course.CreateCourses on a course this person authored that still has no owner_school_id and is not is_master. ' +
      'Teach Enrolled is the enrolled-copy hammer — Create Courses is not required for a class they teach. ' +
      'ReviewProposals opens the school-copy inbox without live mutate. ReviewCatalogProposals does the same for catalog templates. Not a top-nav destination.',
  },
  {
    id: 'grade-rubrics',
    label: 'Grade rubrics',
    description:
      'Grade rubric manager (/grade-rubrics). Global, not per school. One page grant. ' +
      'Create, Edit, and Delete are checkboxes on metadata.rubric_actions. Copy uses Create. ' +
      'Leave the field off to allow all three. An empty list is view only. ' +
      'Foundations are shared: Edit changes every course still using that rubric. ' +
      'Defaults on for Super Admin and for course.CreateMasterCourses or course.EditMasterCourses, with all three actions, when no explicit page_access.grade-rubrics row exists. ' +
      'Course details only selects a rubric and does not need this page. Membership-only (GP/UGP).',
  },
  {
    id: 'student',
    label: 'Student App',
    description:
      'Door into the Acuity Learn student shell (/student/*). Its own chrome — not the staff AppShell, and not in the staff nav. ' +
      'Defaults on with role.IsStudent when no explicit page_access.student deny exists. ' +
      'Today, My Learning, and Calendar default on when this door is open; deny student-today / student-learning / student-calendar ' +
      'to shut one tab down without closing the shell. Reading still needs learning.ViewOwnEnrollments; opening and submitting need their own verbs.',
  },
  {
    id: 'student-today',
    label: 'Today',
    description:
      'Student Today tab. The Student App door already grants this when no explicit page_access.student-today row exists. ' +
      'Deny this key to shut Today down without touching My Learning or Calendar. Reading still needs learning.ViewOwnEnrollments.',
  },
  {
    id: 'student-learning',
    label: 'My Learning',
    description:
      'Student My Learning tab. The Student App door already grants this when no explicit page_access.student-learning row exists. ' +
      'Deny this key to shut My Learning down without touching Today or Calendar. Reading still needs learning.ViewOwnEnrollments.',
  },
  {
    id: 'student-calendar',
    label: 'Calendar',
    description:
      'Student Calendar tab. Dated published work on enrolled courses, plus unfinished items with no due date. ' +
      'The Student App door already grants this when no explicit page_access.student-calendar row exists. ' +
      'Deny this key to shut Calendar down without touching Today or My Learning. Reading still needs learning.ViewOwnEnrollments.',
  },
  {
    id: 'student-progress',
    label: 'Progress',
    description:
      'Retired. The student Progress tab is gone; /student/progress sends them to My Learning. Keep this row so an old UIS deny does not 404. It is not a tab.',
  },
  {
    id: 'parent',
    label: 'Parent view',
    description:
      'Read-only parent home (/parent). Granted on the permanent Parents team (role.IsParent). ' +
      'Does not let the parent do the student\'s work. Membership-only (GP/UGP).',
  },
  {
    id: 'my-students',
    label: 'My Students',
    description:
      'Students linked to this person as a guardian or, at this membership\'s school, as a counselor. ' +
      'The list is this page. parent.ViewStudentChat and the other parent caps decide what appears on a student. ' +
      'Does not default on with a role. Does not open the parent shell. Membership-only (GP/UGP).',
  },
  {
    id: 'library',
    label: 'Library',
    description:
      'Staff Library (/library) — uploads, URLs, later approved catalogs. Must be awarded — Super Admin does not imply it. ' +
      'Defaults on when library.ViewLibrary, library.ViewOthersSources, library.AddSources, library.EditSources, library.ProposeSources, library.ReviewSources, ' +
      'library.RemoveSources, library.AnalyzeSources, or library.ManageApprovedCatalogs is allowed and no explicit page_access.library deny exists. ' +
      'Viewing still needs library.ViewLibrary. Other people\'s sources need library.ViewOthersSources. Add is library.AddSources; live edit / publish is library.EditSources; propose is library.ProposeSources. ' +
      'AI analysis on add is library.AnalyzeSources. ' +
      'Not the student tutor and not Course Builder. Membership-only (GP/UGP).',
  },
  {
    id: 'role-defaults',
    label: 'Role defaults',
    description:
      'Acuity Learn role default grants (/role-defaults). Recipe of pages and capabilities to stamp onto each identity ' +
      '(Teacher, Student, …) when people are added. Does not itself grant those caps. ' +
      'school.OverrideAddUserPermissions is the gear that can uncheck or add keys for one person when adding them. ' +
      'Defaults on with role.IsSuperAdmin when no explicit page_access.role-defaults deny exists. ' +
      'Must be awarded in UIS for anyone else. Membership-only (GP/UGP).',
  },
].map((entry) => ({
  id: entry.id,
  label: entry.label || humanizePermissionKey(entry.id),
  ...(entry.description ? { description: entry.description } : {}),
}));

const ACUITYDMS_PAGES = [
  {
    id: 'home',
    label: 'Home',
    description:
      'AcuityDMS home. Requires an explicit platform_access allow. Defaults on after that unless page_access.home is denied.',
  },
  {
    id: 'profile',
    label: 'Profile',
    description:
      'AcuityDMS profile. Requires an explicit platform_access allow. Defaults on after that unless page_access.profile is denied.',
  },
  {
    id: 'doc-builder',
    label: 'Doc Builder',
    description:
      'PDF document builder. Requires an explicit platform_access allow. An explicit page_access deny for doc-builder (or the former workspace key) blocks this page.',
  },
];

/** @type {Record<string, { pages: {id:string,label:string}[], capabilities: {id:string,label:string}[], pageConfigurations: {id:string,label:string}[] }>} */
export const PLATFORM_CATALOGS = {
  acuitycte: {
    pages: ACUITYCTE_PAGES,
    capabilities: ACUITYCTE_CAPABILITIES,
    pageConfigurations: ACUITYCTE_PAGE_CONFIGURATIONS,
  },
  learnpath: {
    pages: LEARNPATH_PAGES,
    capabilities: LEARNPATH_CAPABILITIES,
    pageConfigurations: LEARNPATH_PAGE_CONFIGURATIONS,
  },
  acuitylearn: {
    pages: ACUITYLEARN_PAGES,
    capabilities: ACUITYLEARN_CAPABILITIES,
    pageConfigurations: [],
  },
  ots: { pages: [], capabilities: [], pageConfigurations: [] },
  acuitydms: {
    pages: ACUITYDMS_PAGES,
    capabilities: [],
    pageConfigurations: [],
  },
};

export const PLATFORM_PERMISSION_TYPES = [
  {
    id: 'platform_access',
    label: 'Platform access',
    hint: 'Master enable for this product. Without allow, other grants do not apply.',
    enabled: true,
  },
  {
    id: 'page_access',
    label: 'Page access',
    hint: 'Child route access (still requires platform_access).',
    enabled: true,
  },
  {
    id: 'page_configuration',
    label: 'Page configuration',
    hint: 'Child page config (still requires platform_access).',
    enabled: true,
  },
  {
    id: 'capability',
    label: 'Capability',
    hint: 'Verb / notify / review flags (still requires platform_access).',
    enabled: true,
  },
];

export const PLATFORM_PERMISSION_TYPES_ENABLED = PLATFORM_PERMISSION_TYPES.filter(
  (t) => t.enabled
);

export function getPlatformProduct(productId) {
  return PLATFORM_PRODUCTS.find((p) => p.id === productId) || null;
}

export function getPlatformCatalog(productId) {
  return (
    PLATFORM_CATALOGS[productId] || {
      pages: [],
      capabilities: [],
      pageConfigurations: [],
    }
  );
}

export function getPlatformKeysForType(productId, permissionType) {
  const catalog = getPlatformCatalog(productId);
  if (permissionType === 'page_access') return catalog.pages;
  if (permissionType === 'capability') return catalog.capabilities;
  if (permissionType === 'page_configuration') return catalog.pageConfigurations;
  return [];
}

/** Old Acuity Learn catalog ids still stored on grants → current keys. */
export const ACUITYLEARN_CAPABILITY_ALIASES = {
  'parent.ContactSchoolAdmin': 'parent.MessageStudentsOffice',
};

export const ACUITYLEARN_PAGE_ALIASES = {
  classes: 'teach',
  'parent-messages': 'messages',
};

export function canonicalPlatformResourceId(productId, permissionType, resourceId) {
  const id = String(resourceId || '');
  if (!id) return id;
  if (productId === 'acuitylearn' && permissionType === 'capability') {
    return ACUITYLEARN_CAPABILITY_ALIASES[id] || id;
  }
  if (productId === 'acuitylearn' && permissionType === 'page_access') {
    return ACUITYLEARN_PAGE_ALIASES[id] || id;
  }
  return id;
}

export function labelForPlatformKey(productId, permissionType, resourceId) {
  if (permissionType === 'platform_access') return 'Platform access';
  const id = canonicalPlatformResourceId(productId, permissionType, resourceId);
  const keys = getPlatformKeysForType(productId, permissionType);
  return keys.find((k) => k.id === id)?.label || id || '—';
}

/** Optional catalog description shown in PPM when a key is selected. */
export function descriptionForPlatformKey(productId, permissionType, resourceId) {
  if (!resourceId || permissionType === 'platform_access') return '';
  const id = canonicalPlatformResourceId(productId, permissionType, resourceId);
  const keys = getPlatformKeysForType(productId, permissionType);
  return keys.find((k) => k.id === id)?.description || '';
}
